<template>
  <div class="schedule-manager">
    <!-- Cabeçalho + ajuda -->
    <div class="schedule-header">
      <div>
        <h5 class="schedule-title">{{ $t('schedule.space.title') }}</h5>
        <p class="schedule-subtitle">{{ $t('schedule.space.subtitle') }}</p>
      </div>
      <div v-if="!readonly" class="schedule-header-actions">
        <b-button variant="outline-primary" size="sm" @click="addRow()">
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          <span class="align-middle">{{ $t('schedule.grid.add_row') }}</span>
        </b-button>
      </div>
    </div>

    <!-- Dado gravado num formato que a grade não representa (ver hydrate) -->
    <div v-if="legacyIgnored > 0" class="schedule-legacy">
      <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50" />
      <span>{{ $t('schedule.space.legacy_ignored', { n: legacyIgnored }) }}</span>
    </div>

    <!-- Estado vazio -->
    <div v-if="isRowEmpty" class="schedule-empty">
      <feather-icon icon="ClockIcon" size="28" class="text-primary mb-1" />
      <p class="schedule-empty-title">{{ $t('schedule.grid.empty_title') }}</p>
      <p class="schedule-empty-hint">{{ $t('schedule.space.empty_hint') }}</p>
      <b-button v-if="!readonly" variant="primary" size="sm" class="mt-1" @click="addRow()">
        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
        <span class="align-middle">{{ $t('schedule.grid.add_row') }}</span>
      </b-button>
    </div>

    <!-- Grade -->
    <div v-else class="schedule-scroll">
      <div class="schedule-table">
        <!-- Dias da semana -->
        <div class="schedule-row schedule-row-head">
          <div class="schedule-legend"></div>
          <div
            v-for="day in weekDays"
            :key="`head-${day}`"
            class="schedule-cell schedule-head-cell"
            :class="{ 'is-weekend': isWeekend(day) }">
            <span class="head-short">{{ dayShort(day) }}</span>
            <span class="head-count">{{ dayCountLabel(day) }}</span>
          </div>
          <div class="schedule-total-col">{{ $t('schedule.weekly_total') }}</div>
          <div class="schedule-actions-col">{{ $t('actions') }}</div>
        </div>

        <!-- Faixas de horário -->
        <div
          v-for="(row, rowIndex) in rows"
          :key="row.uid"
          class="schedule-row schedule-row-body">
          <div class="schedule-legend">
            <span class="legend-item">{{ $t('schedule.grid.start') }}</span>
            <span class="legend-item">{{ $t('schedule.grid.end') }}</span>
            <span
              class="legend-item legend-capacity"
              v-tooltip.auto="{ content: $t('schedule.space.capacity_hint'), delay: { show: 300, hide: 100 } }">
              {{ $t('schedule.space.capacity_label') }}
              <feather-icon icon="HelpCircleIcon" size="12" />
            </span>
            <span class="legend-meta"></span>
          </div>

          <div
            v-for="day in weekDays"
            :key="`${row.uid}-${day}`"
            class="schedule-cell"
            :class="{ 'is-weekend': isWeekend(day), 'has-error': cellHasError(rowIndex, day) }">
            <input
              type="time"
              v-model="row.days[day].start"
              class="cell-input"
              :class="inputClass(rowIndex, day, 'start')"
              :disabled="readonly"
              :aria-label="`${$t('schedule.grid.start')} — ${dayLong(day)}`"
              @change="onStartChange(row, day)"
              @keydown.enter.prevent="focusNextField($event)" />
            <input
              type="time"
              v-model="row.days[day].end"
              class="cell-input"
              :class="inputClass(rowIndex, day, 'end')"
              :disabled="readonly"
              :aria-label="`${$t('schedule.grid.end')} — ${dayLong(day)}`"
              @keydown.enter.prevent="focusNextField($event)" />
            <input
              type="number"
              min="1"
              max="999"
              step="1"
              v-model="row.days[day].capacity"
              class="cell-input cell-capacity"
              :class="inputClass(rowIndex, day, 'capacity')"
              :disabled="readonly"
              :aria-label="`${$t('schedule.space.capacity_label')} — ${dayLong(day)}`"
              v-tooltip.auto="{ content: $t('schedule.space.capacity_hint'), delay: { show: 500, hide: 100 } }" />
            <span class="cell-meta">{{ cellMeta(rowIndex, day) }}</span>
          </div>

          <div class="schedule-total-col">
            <span class="row-total">{{ rowTotals[rowIndex] }}</span>
            <span class="row-days">{{ rowDaysLabel(rowIndex) }}</span>
          </div>

          <div class="schedule-actions-col">
            <template v-if="!readonly">
              <feather-icon
                icon="CopyIcon"
                size="20"
                class="cursor-pointer text-primary"
                @click="openReplicate(rowIndex)"
                v-tooltip.auto="{ content: $t('schedule.grid.replicate'), delay: { show: 300, hide: 100 } }" />
              <feather-icon
                icon="LayersIcon"
                size="20"
                class="cursor-pointer text-primary"
                @click="duplicateRow(rowIndex)"
                v-tooltip.auto="{ content: $t('schedule.grid.duplicate_row'), delay: { show: 300, hide: 100 } }" />
              <feather-icon
                icon="Trash2Icon"
                size="20"
                class="cursor-pointer text-danger"
                @click="confirmRemoveRow(rowIndex)"
                v-tooltip.auto="{ content: $t('schedule.grid.remove_row'), delay: { show: 300, hide: 100 } }" />
            </template>
          </div>

          <ul v-if="rowMessages(rowIndex).length" class="schedule-row-messages">
            <li v-for="(message, index) in rowMessages(rowIndex)" :key="`msg-${row.uid}-${index}`">
              <feather-icon icon="AlertCircleIcon" size="13" class="mr-25" />
              <strong>{{ dayShort(message.day) }}:</strong> {{ message.text }}
            </li>
          </ul>
        </div>

        <!-- Total por dia -->
        <div class="schedule-row schedule-row-foot">
          <div class="schedule-legend">{{ $t('schedule.day_total') }}</div>
          <div
            v-for="day in weekDays"
            :key="`foot-${day}`"
            class="schedule-cell schedule-foot-cell"
            :class="{ 'is-weekend': isWeekend(day) }">
            {{ dayTotals[day] }}
          </div>
          <div class="schedule-total-col">{{ grandTotal }}</div>
          <div class="schedule-actions-col"></div>
        </div>
      </div>
    </div>

    <!-- Resumo / estado da validação -->
    <div class="schedule-status" :class="{ 'is-invalid': hasErrors }">
      <template v-if="hasErrors">
        <feather-icon icon="AlertCircleIcon" size="16" class="text-danger mr-50" />
        <span class="text-danger">{{ $t('schedule.grid.fix_errors') }}</span>
      </template>
      <template v-else-if="!isRowEmpty">
        <feather-icon icon="InfoIcon" size="16" class="text-primary mr-50" />
        <span>{{ summaryLabel }}</span>
      </template>
    </div>

    <!-- Replicar para outros dias -->
    <b-modal
      v-model="replicate.open"
      :title="$t('schedule.grid.replicate_modal.title')"
      size="md"
      centered>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="replicate.open = false">
          {{ $t('schedule.grid.replicate_modal.cancel') }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="!replicate.source || replicate.targets.length === 0"
          @click="applyReplicate">
          {{ $t('schedule.grid.replicate_modal.confirm') }}
        </b-button>
      </template>

      <p v-if="replicateSourceOptions.length === 0" class="text-danger mb-0">
        {{ $t('schedule.grid.replicate_modal.no_source') }}
      </p>

      <template v-else>
        <b-form-group :label="$t('schedule.grid.replicate_modal.source')">
          <select v-model="replicate.source" class="form-control">
            <option
              v-for="option in replicateSourceOptions"
              :key="`src-${option.day}`"
              :value="option.day">
              {{ option.label }}
            </option>
          </select>
        </b-form-group>

        <b-form-group :label="$t('schedule.grid.replicate_modal.targets')">
          <div class="replicate-quick">
            <b-button variant="flat-primary" size="sm" @click="pickTargets('all')">
              {{ $t('schedule.grid.replicate_modal.all') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('weekdays')">
              {{ $t('schedule.grid.replicate_modal.weekdays') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('weekend')">
              {{ $t('schedule.grid.replicate_modal.weekend') }}
            </b-button>
            <b-button variant="flat-primary" size="sm" @click="pickTargets('none')">
              {{ $t('schedule.grid.replicate_modal.none') }}
            </b-button>
          </div>
          <div class="replicate-days">
            <label
              v-for="day in replicateTargetDays"
              :key="`tgt-${day}`"
              class="replicate-day">
              <input
                type="checkbox"
                :checked="replicate.targets.indexOf(day) !== -1"
                @change="toggleTarget(day)" />
              <span>{{ dayLong(day) }}</span>
              <span v-if="isTargetFilled(day)" class="replicate-day-tag">
                {{ $t('schedule.grid.replicate_modal.filled') }}
              </span>
            </label>
          </div>
        </b-form-group>

        <label class="replicate-day">
          <input type="checkbox" v-model="replicate.overwrite" />
          <span>{{ $t('schedule.grid.replicate_modal.overwrite') }}</span>
        </label>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BFormGroup, BModal } from 'bootstrap-vue'

const WEEK_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const WEEKEND_DAYS = ['saturday', 'sunday']
const DAY_NAMES_PT = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo']
const DAY_NAMES_SHORT_PT = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom']

let uidSeq = 0

export default {
  name: 'ScheduleManager',

  components: {
    BButton,
    BFormGroup,
    BModal,
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      weekDays: WEEK_DAYS,
      rows: [],
      legacyIgnored: 0,
      // Assinatura do último payload emitido: o watcher de `value` usa isso para
      // não re-hidratar a grade com o que o próprio componente acabou de emitir
      // (era o que exigia o vaivém de flags isUpdating/isEmitting).
      lastEmitted: null,
      replicate: {
        open: false,
        rowIndex: null,
        source: null,
        targets: [],
        overwrite: false
      }
    }
  },

  computed: {
    isRowEmpty() {
      return this.rows.length === 0
    },
    /**
     * Validação de toda a grade, por célula. Sobreposição depende das outras
     * linhas, então tudo é reavaliado junto a cada digitação.
     */
    validation() {
      const result = {}
      const byDay = {}

      this.rows.forEach((row, rowIndex) => {
        result[rowIndex] = {}

        WEEK_DAYS.forEach(day => {
          const cell = row.days[day]
          const entry = { errors: [] }
          result[rowIndex][day] = entry

          const start = normalizeTime(cell.start)
          const end = normalizeTime(cell.end)
          const capacity = String(cell.capacity === null || cell.capacity === undefined ? '' : cell.capacity).trim()
          const touched = Boolean(start || end || capacity)

          if (!touched) return

          if (!start || !end) {
            entry.errors.push(this.$t('schedule.grid.errors.incomplete'))
          } else if (toMinutes(end) <= toMinutes(start)) {
            entry.errors.push(this.$t('schedule.grid.errors.order'))
          }

          const parsed = Number(capacity)
          if (capacity === '' || !Number.isInteger(parsed) || parsed < 1) {
            entry.errors.push(this.$t('schedule.space.errors.capacity'))
          }

          if (start && end && toMinutes(end) > toMinutes(start)) {
            if (!byDay[day]) byDay[day] = []
            byDay[day].push({ rowIndex, start: toMinutes(start), end: toMinutes(end) })
          }
        })
      })

      // Duas faixas sobrepostas no mesmo dia disputam a mesma capacidade: a
      // disponibilidade pública casa por horário exato e a reserva passaria a
      // depender de qual item vem primeiro no JSON.
      Object.keys(byDay).forEach(day => {
        const slots = byDay[day]
        for (let i = 0; i < slots.length; i++) {
          for (let j = i + 1; j < slots.length; j++) {
            const a = slots[i]
            const b = slots[j]
            if (a.start < b.end && b.start < a.end) {
              const text = this.$t('schedule.grid.errors.overlap', { day: this.dayLong(day) })
              ;[a, b].forEach(slot => {
                const entry = result[slot.rowIndex][day]
                if (!entry.errors.includes(text)) entry.errors.push(text)
              })
            }
          }
        }
      })

      return result
    },
    hasErrors() {
      return this.rows.some((row, rowIndex) =>
        WEEK_DAYS.some(day => this.validation[rowIndex][day].errors.length > 0)
      )
    },
    slotCount() {
      return this.rows.reduce(
        (total, row) => total + WEEK_DAYS.filter(day => isCellFilled(row.days[day])).length,
        0
      )
    },
    activeDaysCount() {
      return WEEK_DAYS.filter(day => this.rows.some(row => isCellFilled(row.days[day]))).length
    },
    summaryLabel() {
      return this.$t('schedule.space.summary', {
        slots: this.slotCount,
        days: this.activeDaysCount,
        total: this.grandTotal
      })
    },
    rowTotals() {
      return this.rows.map(row => formatMinutes(
        WEEK_DAYS.reduce((total, day) => total + cellDuration(row.days[day]), 0)
      ))
    },
    dayTotals() {
      return WEEK_DAYS.reduce((totals, day) => {
        totals[day] = formatMinutes(
          this.rows.reduce((total, row) => total + cellDuration(row.days[day]), 0)
        )
        return totals
      }, {})
    },
    grandTotal() {
      return formatMinutes(
        this.rows.reduce(
          (total, row) => total + WEEK_DAYS.reduce((sum, day) => sum + cellDuration(row.days[day]), 0),
          0
        )
      )
    },
    replicateRow() {
      return this.replicate.rowIndex === null ? null : this.rows[this.replicate.rowIndex]
    },
    replicateSourceOptions() {
      if (!this.replicateRow) return []
      return WEEK_DAYS
        .filter(day => isCellFilled(this.replicateRow.days[day]))
        .map(day => {
          const cell = this.replicateRow.days[day]
          const capacity = String(cell.capacity || '').trim()
          const suffix = capacity ? ` · ${capacity} ${this.$t('schedule.space.capacity_unit')}` : ''
          return {
            day,
            label: `${this.dayLong(day)} · ${normalizeTime(cell.start)} – ${normalizeTime(cell.end)}${suffix}`
          }
        })
    },
    replicateTargetDays() {
      return WEEK_DAYS.filter(day => day !== this.replicate.source)
    }
  },

  watch: {
    value: {
      handler(incoming) {
        const signature = JSON.stringify(incoming || [])
        if (signature === this.lastEmitted) return
        this.hydrate(incoming)
      },
      immediate: true
    },
    rows: {
      handler() {
        this.emitSchedule()
      },
      deep: true
    },
    hasErrors: {
      handler(value) {
        this.$emit('validity-change', !value)
      },
      immediate: true
    }
  },

  methods: {
    // ---------- rótulos ----------
    dayShort(day) {
      return this.$t(`schedule.weekday_short.${day}`)
    },
    dayLong(day) {
      return this.$t(`schedule.weekday.${day}`)
    },
    isWeekend(day) {
      return WEEKEND_DAYS.includes(day)
    },
    dayCountLabel(day) {
      const count = this.rows.filter(row => isCellFilled(row.days[day])).length
      return count === 0 ? '—' : this.$t('schedule.grid.day_count', { n: count })
    },
    rowDaysLabel(rowIndex) {
      const count = WEEK_DAYS.filter(day => isCellFilled(this.rows[rowIndex].days[day])).length
      return count === 0 ? '' : this.$t('schedule.grid.day_count', { n: count })
    },

    // ---------- validação na tela ----------
    cellHasError(rowIndex, day) {
      return this.validation[rowIndex][day].errors.length > 0
    },
    inputClass(rowIndex, day, field) {
      const cell = this.rows[rowIndex].days[day]
      const value = field === 'capacity' ? cell.capacity : cell[field]
      const filled = String(value === null || value === undefined ? '' : value).trim() !== ''

      return {
        'is-invalid-cell': this.cellHasError(rowIndex, day),
        'is-filled': filled && !this.cellHasError(rowIndex, day),
        'is-empty': !filled
      }
    },
    cellMeta(rowIndex, day) {
      const duration = cellDuration(this.rows[rowIndex].days[day])
      return duration > 0 ? formatMinutes(duration) : ''
    },
    rowMessages(rowIndex) {
      const messages = []
      WEEK_DAYS.forEach(day => {
        this.validation[rowIndex][day].errors.forEach(text => messages.push({ day, text }))
      })
      return messages
    },

    // ---------- edição ----------
    /**
     * Capacidade nasce em 1 junto com o horário: o campo é obrigatório e deixar
     * vazio devolveria um horário que a vitrine mostra como esgotado.
     */
    onStartChange(row, day) {
      const cell = row.days[day]
      if (normalizeTime(cell.start) && String(cell.capacity || '').trim() === '') {
        cell.capacity = '1'
      }
    },
    addRow(atIndex = null) {
      const position = Number.isInteger(atIndex) ? atIndex + 1 : this.rows.length
      this.rows.splice(position, 0, emptyRow())
    },
    duplicateRow(rowIndex) {
      const source = this.rows[rowIndex]
      const copy = emptyRow()

      WEEK_DAYS.forEach(day => {
        copy.days[day].start = source.days[day].start
        copy.days[day].end = source.days[day].end
        copy.days[day].capacity = source.days[day].capacity
      })

      this.rows.splice(rowIndex + 1, 0, copy)
    },
    confirmRemoveRow(rowIndex) {
      const hasContent = WEEK_DAYS.some(day => isCellFilled(this.rows[rowIndex].days[day]))

      if (!hasContent) {
        this.rows.splice(rowIndex, 1)
        return
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('confirmacao'),
        acceptText: this.$t('yes'),
        cancelText: this.$t('no'),
        text: this.$t('schedule.space.remove_row_confirm'),
        accept: () => {
          this.rows.splice(rowIndex, 1)
        }
      })
    },
    focusNextField(event) {
      const inputs = Array.from(this.$el.querySelectorAll("input[type='time']"))
      const currentIndex = inputs.indexOf(event.target)
      if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
        inputs[currentIndex + 1].focus()
      }
    },

    // ---------- replicar ----------
    openReplicate(rowIndex) {
      const row = this.rows[rowIndex]
      const firstFilled = WEEK_DAYS.find(day => isCellFilled(row.days[day])) || null

      this.replicate = {
        open: true,
        rowIndex,
        source: firstFilled,
        targets: [],
        overwrite: false
      }
    },
    pickTargets(preset) {
      const candidates = this.replicateTargetDays

      if (preset === 'all') this.replicate.targets = [...candidates]
      else if (preset === 'weekdays') this.replicate.targets = candidates.filter(day => !this.isWeekend(day))
      else if (preset === 'weekend') this.replicate.targets = candidates.filter(day => this.isWeekend(day))
      else this.replicate.targets = []
    },
    toggleTarget(day) {
      const index = this.replicate.targets.indexOf(day)
      if (index === -1) this.replicate.targets.push(day)
      else this.replicate.targets.splice(index, 1)
    },
    isTargetFilled(day) {
      return this.replicateRow ? isCellFilled(this.replicateRow.days[day]) : false
    },
    applyReplicate() {
      const row = this.replicateRow
      if (!row || !this.replicate.source) return

      const source = row.days[this.replicate.source]
      let applied = 0
      let skipped = 0

      this.replicate.targets.forEach(day => {
        const target = row.days[day]

        if (isCellFilled(target) && !this.replicate.overwrite) {
          skipped++
          return
        }

        target.start = source.start
        target.end = source.end
        target.capacity = source.capacity
        applied++
      })

      this.replicate.open = false

      const parts = [this.$t('schedule.grid.replicate_modal.done', { n: applied })]
      if (skipped) parts.push(this.$t('schedule.grid.replicate_modal.skipped', { n: skipped }))
      this.notifySuccess(this.$vs, parts.join(' '))
    },

    // ---------- entrada/saída de dados ----------
    /**
     * Aceita os formatos que já existem gravados em `schedule_weekdays`: o dia
     * pode vir como nome em inglês, índice numérico ou rótulo em pt-BR.
     */
    resolveDay(item) {
      if (typeof item.dayOfWeekIndex === 'number' && WEEK_DAYS[item.dayOfWeekIndex]) {
        return WEEK_DAYS[item.dayOfWeekIndex]
      }
      if (typeof item.dayOfWeek === 'number' && WEEK_DAYS[item.dayOfWeek]) {
        return WEEK_DAYS[item.dayOfWeek]
      }
      if (typeof item.dayOfWeek === 'string') {
        const key = item.dayOfWeek.toLowerCase()
        if (WEEK_DAYS.includes(key)) return key
      }
      if (typeof item.dayName === 'string') {
        const index = DAY_NAMES_PT.indexOf(item.dayName)
        if (index !== -1) return WEEK_DAYS[index]
      }
      if (typeof item.shortName === 'string') {
        const index = DAY_NAMES_SHORT_PT.indexOf(item.shortName.toLowerCase())
        if (index !== -1) return WEEK_DAYS[index]
      }
      if (typeof item.day === 'string' && WEEK_DAYS.includes(item.day.toLowerCase())) {
        return item.day.toLowerCase()
      }
      if (typeof item.day === 'number' && WEEK_DAYS[item.day]) {
        return WEEK_DAYS[item.day]
      }
      return null
    },
    hydrate(scheduleData) {
      const items = Array.isArray(scheduleData) ? scheduleData : []
      this.legacyIgnored = 0

      if (items.length === 0) {
        this.rows = []
        return
      }

      const byRow = {}
      items.forEach((item, index) => {
        // Há espaço gravado com `schedule_weekdays` = [1,2,3,4,5] (só o índice
        // do dia, sem horário). A disponibilidade pública já ignora esse
        // formato — a grade avisa em vez de inventar horário.
        const day = item && typeof item === 'object' ? this.resolveDay(item) : null
        if (!day || !normalizeTime(item.startTime) || !normalizeTime(item.endTime)) {
          this.legacyIgnored++
          return
        }

        const rowIndex = typeof item.rowIndex === 'number' ? item.rowIndex : 0
        if (!byRow[rowIndex]) byRow[rowIndex] = emptyRow()

        // Horário repetido no mesmo dia e linha (dado antigo): abre uma linha
        // extra em vez de sobrescrever em silêncio.
        let target = byRow[rowIndex]
        while (isCellFilled(target.days[day])) {
          const overflowKey = `overflow-${rowIndex}-${index}`
          if (!byRow[overflowKey]) byRow[overflowKey] = emptyRow()
          target = byRow[overflowKey]
        }

        target.days[day] = {
          start: normalizeTime(item.startTime),
          end: normalizeTime(item.endTime),
          capacity: item.maxUsers === null || item.maxUsers === undefined ? '1' : String(item.maxUsers)
        }
      })

      this.rows = Object.keys(byRow)
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }))
        .map(key => byRow[key])
        .filter(row => WEEK_DAYS.some(day => isCellFilled(row.days[day])))
    },
    /**
     * Formato de saída preservado: `schedule_weekdays` é lido pela vitrine e
     * pela disponibilidade pública por `dayOfWeek`/`startTime`/`endTime`/
     * `maxUsers`, e o `rowIndex` é o que remonta as faixas na volta.
     */
    buildScheduleData() {
      const result = []

      this.rows.forEach((row, rowIndex) => {
        WEEK_DAYS.forEach((day, dayIndex) => {
          const cell = row.days[day]
          if (!isCellFilled(cell)) return

          const capacity = parseInt(cell.capacity, 10)

          result.push({
            dayOfWeek: day,
            dayOfWeekIndex: dayIndex,
            dayName: DAY_NAMES_PT[dayIndex],
            shortName: DAY_NAMES_SHORT_PT[dayIndex],
            startTime: normalizeTime(cell.start),
            endTime: normalizeTime(cell.end),
            maxUsers: Number.isInteger(capacity) && capacity > 0 ? capacity : 1,
            rowIndex
          })
        })
      })

      return result
    },
    /**
     * Emite sempre, inclusive lista vazia. A versão anterior barrava o payload
     * vazio "para não perder dados" — o efeito era não conseguir apagar o
     * último horário: a tela ficava limpa e o espaço continuava aberto.
     */
    emitSchedule() {
      const scheduleData = this.buildScheduleData()
      this.lastEmitted = JSON.stringify(scheduleData)
      this.$emit('input', scheduleData)
      this.$emit('change', scheduleData)
    },
    // API pública mantida para quem usa o componente por ref
    getScheduleData() {
      return this.buildScheduleData()
    },
    isValid() {
      return !this.hasErrors
    }
  }
}

// ---------- helpers ----------
function emptyRow() {
  uidSeq += 1
  return {
    uid: `space-row-${uidSeq}`,
    days: WEEK_DAYS.reduce((days, day) => {
      days[day] = { start: '', end: '', capacity: '' }
      return days
    }, {})
  }
}

function normalizeTime(value) {
  const text = String(value === null || value === undefined ? '' : value).trim()
  return /^\d{2}:\d{2}/.test(text) ? text.slice(0, 5) : ''
}

function toMinutes(time) {
  const [hours, minutes] = normalizeTime(time).split(':').map(Number)
  return (hours * 60) + minutes
}

function formatMinutes(totalMinutes) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function isCellFilled(cell) {
  return Boolean(normalizeTime(cell.start) && normalizeTime(cell.end))
}

function cellDuration(cell) {
  if (!isCellFilled(cell)) return 0
  const duration = toMinutes(cell.end) - toMinutes(cell.start)
  return duration > 0 ? duration : 0
}
</script>

<style lang="scss" scoped>
.schedule-manager {
  padding: 0.5rem 0 0;
}

.schedule-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.schedule-title {
  margin: 0 0 0.15rem;
  font-weight: 600;
}

.schedule-subtitle {
  margin: 0;
  max-width: 62ch;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.schedule-legacy {
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
  margin-bottom: 1rem;
  padding: 0.65rem 0.9rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 159, 67, 0.5);
  background: rgba(255, 159, 67, 0.08);
  color: #b26a1c;
  font-size: 0.82rem;
}

.schedule-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.25rem 1rem;
  border: 1px dashed #d8d6de;
  border-radius: 0.5rem;
}

.schedule-empty-title {
  margin: 0;
  font-weight: 600;
}

.schedule-empty-hint {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: #6e6b7b;
}

.schedule-scroll {
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.schedule-table {
  min-width: 940px;
}

.schedule-row {
  display: grid;
  grid-template-columns: 112px repeat(7, minmax(96px, 1fr)) 104px 104px;
  gap: 0.5rem;
  align-items: start;
  padding: 0.5rem 0;
}

.schedule-row-body {
  border-top: 1px solid #ebe9f1;
}

.schedule-row-head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  font-weight: 600;
}

.schedule-row-foot {
  border-top: 1px solid #ebe9f1;
  font-weight: 600;
  background: #fafafc;
}

.schedule-legend {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: #6e6b7b;
  padding-top: 0.15rem;
}

.legend-item {
  height: 2.15rem;
  display: flex;
  align-items: center;
}

.legend-capacity {
  gap: 0.2rem;
  cursor: help;
}

.legend-meta {
  height: 1.1rem;
}

.schedule-cell {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  align-items: center;
  border-radius: 0.35rem;
  padding: 0.15rem 0.15rem 0;
}

.schedule-cell.is-weekend {
  background: rgba(115, 103, 240, 0.04);
}

.schedule-cell.has-error {
  background: rgba(234, 84, 85, 0.06);
}

.schedule-head-cell,
.schedule-foot-cell {
  justify-content: center;
  text-align: center;
}

.head-short {
  font-size: 0.9rem;
}

.head-count {
  font-size: 0.7rem;
  font-weight: 400;
  color: #6e6b7b;
}

.cell-input {
  width: 100%;
  height: 2.15rem;
  padding: 0.25rem 0.4rem;
  border: 1px solid #d8d6de;
  border-radius: 0.35rem;
  font-size: 0.85rem;
  color: #6e6b7b;
  background: #fff;
}

.cell-input:focus {
  outline: none;
  border-color: #7367f0;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.15);
}

.cell-input:disabled {
  background: #f8f8f8;
}

.cell-input.is-filled {
  border-color: #28c76f;
  background: rgba(40, 199, 111, 0.08);
}

.cell-input.is-invalid-cell {
  border-color: #ea5455;
  background: rgba(234, 84, 85, 0.08);
}

.cell-capacity {
  text-align: center;
}

.cell-meta {
  height: 1.1rem;
  font-size: 0.7rem;
  color: #b9b9c3;
}

.schedule-total-col,
.schedule-actions-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.15rem;
  padding-top: 0.15rem;
  min-height: 2.15rem;
}

.schedule-actions-col {
  flex-direction: row;
  gap: 0.5rem;
  align-items: flex-start;
  padding-top: 0.35rem;
}

.row-total {
  font-weight: 600;
}

.row-days {
  font-size: 0.7rem;
  color: #b9b9c3;
}

.schedule-row-messages {
  grid-column: 1 / -1;
  list-style: none;
  margin: 0.25rem 0 0;
  padding: 0;
  font-size: 0.78rem;
  color: #ea5455;
}

.schedule-row-messages li {
  display: flex;
  align-items: center;
}

.schedule-status {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: #fafafc;
  border: 1px solid #ebe9f1;
  font-size: 0.85rem;
  min-height: 3rem;
}

.schedule-status.is-invalid {
  border-color: rgba(234, 84, 85, 0.5);
  background: rgba(234, 84, 85, 0.06);
}

.replicate-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.replicate-days {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 1rem;
}

.replicate-day {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-weight: 400;
  cursor: pointer;
}

.replicate-day input {
  cursor: pointer;
}

.replicate-day-tag {
  font-size: 0.7rem;
  color: #ff9f43;
}
</style>
